/*
 * Theme chips: toggleable collection filters.
 *
 * - "filter value" chips and "price" chips are applied / removed through the existing collection
 *   filtering (FacetFiltersForm in facets.js), so the sidebar, sorting, active-filter pills, product
 *   count, Load More state, URL and browser history all stay on Shopify's native AJAX pipeline.
 *   Every request is built from the live URL with only this chip's own parameter changed, so unrelated
 *   filters and sorting are never cleared.
 * - "link" chips (e.g. a theme collection such as /collections/love) are toggles too:
 *     inactive -> go to that collection, carrying the active filters and sorting along, and remember
 *                 which collection the customer came from;
 *     active   -> clicking again leaves it: back to the remembered collection (or the "all products"
 *                 collection when the customer arrived directly), again carrying the other filters.
 *     A link chip whose URL is the current collection plus filter parameters is a plain filter toggle
 *     and is applied / removed in place by AJAX.
 * If AJAX filtering is unavailable, the href is followed as a normal navigation.
 */
(function () {
  if (window.ttjThemeChipsLoaded) return;
  window.ttjThemeChipsLoaded = true;

  const STORAGE_KEY = 'ttjThemeChipOrigin';
  const MAX_AGE_MS = 6 * 60 * 60 * 1000;
  // Never carried between requests: pagination belongs to one result set, and section_id is only the
  // transport of Shopify's section rendering (it can leak into the URL from fetched pagination links).
  const TRANSIENT_PARAMS = ['page', 'section_id'];

  let saved = null;

  const norm = function (value) {
    return String(value === null || value === undefined ? '' : value)
      .trim()
      .toLowerCase();
  };

  const currentParams = function () {
    const params = new URLSearchParams(window.location.search);
    TRANSIENT_PARAMS.forEach(function (key) {
      params.delete(key);
    });
    return params;
  };

  // Collection path without a language / market prefix (/en-in/collections/love -> /collections/love).
  const collectionPath = function (path) {
    let clean = path;
    try {
      clean = decodeURIComponent(path);
    } catch (error) {
      clean = path;
    }
    clean = clean.toLowerCase().replace(/\/+$/, '');
    const at = clean.indexOf('/collections/');
    if (at > 0) clean = clean.slice(at);
    return clean;
  };

  const parseHref = function (chip) {
    try {
      return new URL(chip.getAttribute('href'), window.location.origin);
    } catch (error) {
      return null;
    }
  };

  const chipFilterPairs = function (url) {
    return Array.from(url.searchParams.entries()).filter(function (pair) {
      return pair[0] !== 'sort_by' && TRANSIENT_PARAMS.indexOf(pair[0]) === -1;
    });
  };

  const readOrigin = function () {
    try {
      const data = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!data || !data.base || !data.via || Date.now() - data.time > MAX_AGE_MS) return null;
      return data;
    } catch (error) {
      return null;
    }
  };

  const writeOrigin = function (data) {
    try {
      if (data) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* storage unavailable: the "all products" fallback is used instead */
    }
  };

  const linkChipPaths = function () {
    return Array.from(document.querySelectorAll('[data-theme-chip][data-chip-type="link"]'))
      .map(parseHref)
      .filter(Boolean)
      .map(function (url) {
        return collectionPath(url.pathname);
      });
  };

  const renderInPlace = function (chip, params) {
    TRANSIENT_PARAMS.forEach(function (key) {
      params.delete(key);
    });
    const list = chip.closest('.ttj-theme-chips__list');
    saved = { id: chip.dataset.chipId, scroll: list ? list.scrollLeft : 0 };
    FacetFiltersForm.renderPage(params.toString());
  };

  const navigate = function (path, params) {
    const query = params.toString();
    window.location.assign(path + (query ? '?' + query : ''));
  };

  // Returns true when the click was handled here (default navigation prevented).
  const handleLinkChip = function (chip, event) {
    const target = parseHref(chip);
    if (!target || target.origin !== window.location.origin) return false;

    const targetPath = collectionPath(target.pathname);
    const here = collectionPath(window.location.pathname);
    const pairs = chipFilterPairs(target);
    const active = chip.classList.contains('is-active');
    const canAjax = typeof FacetFiltersForm !== 'undefined' && !!document.getElementById('product-grid');

    // Same collection + filter parameters: an ordinary filter toggle, done in place.
    if (targetPath === here && pairs.length > 0 && canAjax) {
      const current = currentParams();
      const params = new URLSearchParams();
      current.forEach(function (value, key) {
        const isChipPair = pairs.some(function (pair) {
          return pair[0] === key && norm(pair[1]) === norm(value);
        });
        if (active && isChipPair) return;
        params.append(key, value);
      });
      if (!active) {
        pairs.forEach(function (pair) {
          const present = params.getAll(pair[0]).some(function (value) {
            return norm(value) === norm(pair[1]);
          });
          if (!present) params.append(pair[0], pair[1]);
        });
      }
      event.preventDefault();
      renderInPlace(chip, params);
      return true;
    }

    // Active collection chip clicked again: leave that collection.
    if (targetPath === here && pairs.length === 0 && active) {
      const origin = readOrigin();
      const fallback = (chip.closest('[data-chip-fallback-url]') || document.body).dataset.chipFallbackUrl;
      let destination = fallback || '';
      if (origin && collectionPath(origin.via) === here && collectionPath(origin.base) !== here) {
        destination = origin.base;
      }
      if (!destination || collectionPath(destination) === here) return false;
      writeOrigin(null);
      event.preventDefault();
      navigate(destination, currentParams());
      return true;
    }

    // Inactive chip: go to the chip's collection, keeping the other filters and sorting.
    if (targetPath !== here || pairs.length > 0) {
      const stored = readOrigin();
      const onAChipPage = linkChipPaths().indexOf(here) !== -1;
      const base = onAChipPage && stored ? stored.base : window.location.pathname;
      writeOrigin({ base: base, via: target.pathname, time: Date.now() });

      const params = currentParams();
      const chipKeys = {};
      target.searchParams.forEach(function (value, key) {
        chipKeys[key] = true;
      });
      Object.keys(chipKeys).forEach(function (key) {
        params.delete(key);
      });
      target.searchParams.forEach(function (value, key) {
        if (TRANSIENT_PARAMS.indexOf(key) === -1) params.append(key, value);
      });
      event.preventDefault();
      navigate(target.pathname, params);
      return true;
    }

    return false;
  };

  document.addEventListener('click', function (event) {
    const chip = event.target.closest('[data-theme-chip]');
    if (!chip) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (chip.dataset.chipType === 'link') {
      handleLinkChip(chip, event);
      return;
    }

    if (typeof FacetFiltersForm === 'undefined' || !document.getElementById('product-grid')) return;

    let params;
    if (chip.dataset.chipType === 'price') {
      params = currentParams();
      const maxParam = chip.dataset.priceMaxParam;
      const minParam = chip.dataset.priceMinParam;
      if (chip.dataset.active === 'true') {
        params.delete(maxParam);
        params.delete(minParam);
      } else {
        params.set(maxParam, chip.dataset.priceMax);
        if (chip.dataset.priceMin) params.set(minParam, chip.dataset.priceMin);
        else params.delete(minParam);
      }
    } else if (chip.dataset.chipType === 'filter_value' && chip.dataset.filterParam) {
      const name = chip.dataset.filterParam;
      const value = chip.dataset.filterValue;
      const current = currentParams();
      // Same case-insensitive comparison as the highlight below, so a chip that looks selected
      // is always removed by clicking it (and every duplicate of the value is dropped).
      const isActive = current.getAll(name).some(function (v) {
        return norm(v) === norm(value);
      });
      params = new URLSearchParams();
      current.forEach(function (v, k) {
        if (isActive && k === name && norm(v) === norm(value)) return;
        params.append(k, v);
      });
      if (!isActive) params.append(name, value);
    } else {
      const href = chip.getAttribute('href') || '';
      const query = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1).split('#')[0];
      params = new URLSearchParams(query);
    }

    event.preventDefault();
    renderInPlace(chip, params);
  });

  // Highlight chips from the live URL (the source of truth shared with the sidebar), so a
  // chosen chip is always shown as selected, on load, after any filter change and on back/forward.
  function syncActive() {
    const params = new URLSearchParams(window.location.search);
    document.querySelectorAll('[data-theme-chip]').forEach(function (chip) {
      let active = null;
      if (chip.dataset.chipType === 'filter_value' && chip.dataset.filterParam) {
        const want = norm(chip.dataset.filterValue);
        active = params.getAll(chip.dataset.filterParam).some(function (v) {
          return norm(v) === want;
        });
      } else if (chip.dataset.chipType === 'link') {
        // Custom-URL chips: highlighted when the chip points at the page being viewed
        // (same collection path) and every filter in its URL is applied.
        const target = parseHref(chip);
        if (!target) return;
        const wanted = chipFilterPairs(target);
        active =
          collectionPath(target.pathname) === collectionPath(window.location.pathname) &&
          wanted.every(function (pair) {
            return params.getAll(pair[0]).some(function (v) {
              return norm(v) === norm(pair[1]);
            });
          });
      } else if (chip.dataset.chipType === 'price') {
        const max = params.get(chip.dataset.priceMaxParam);
        const min = params.get(chip.dataset.priceMinParam);
        active =
          max !== null &&
          parseFloat(max) === parseFloat(chip.dataset.priceMax) &&
          (parseFloat(min || '0') || 0) === (parseFloat(chip.dataset.priceMin || '0') || 0);
        chip.dataset.active = active ? 'true' : 'false';
      }
      if (active === null) return;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  // The chips live inside #ProductGridContainer, which facets.js re-renders; restore the
  // chip row scroll position and keyboard focus afterwards.
  function observe() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    syncActive();
    new MutationObserver(function () {
      syncActive();
      if (!saved) return;
      const list = container.querySelector('.ttj-theme-chips__list');
      if (list) list.scrollLeft = saved.scroll;
      const again = container.querySelector('[data-chip-id="' + saved.id + '"]');
      if (again) again.focus({ preventScroll: true });
      saved = null;
    }).observe(container, { childList: true });
  }

  window.addEventListener('popstate', function () {
    // facets.js re-renders the grid on history navigation; sync once more after it settles.
    setTimeout(syncActive, 0);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observe);
  else observe();
})();
